import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { mergeExistingFields } from "helpers/mergeExistingFields";
import { type RiskKycRulesItem, RiskKycRulesItemResponse, getRiskKycRules } from "../rules/slice";

export interface RiskKycPresetItem {
  id: number,
  code?: string, //"base_cad" "base_usd" "base"
  title?: string,
  description?: string,
  rulesQty?: number;
  isActive: boolean,
  createdAt: string,
  updatedAt: string
};

export interface RiskKycPresetRule {
  id: number,
  presetId: number,
  ruleDefinitionId: number,
  position: number,
  createdAt: string,
  updatedAt: string,
  rule: RiskKycRulesItem
}
export interface RiskKycPresetDraftItem extends RiskKycPresetItem {
  assignedRules?: RiskKycPresetRule[];
}

interface RiskKeycPresetState {
  list: RiskKycPresetItem[];
  activePage: number;
  totalItems: number;
  totalPages: number;
  isListFetched: boolean;
  selectedItems: Array<RiskKycPresetItem['id']>;
  isItemFetching: boolean;
  isItemFetched: boolean;
  draftItem: Partial<RiskKycPresetDraftItem>;
  processingItemId?: number;
}

const initialState: RiskKeycPresetState = {
  list: [],
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  isListFetched: false,
  selectedItems: [],
  isItemFetching: false,
  isItemFetched: false,
  processingItemId: undefined,
  draftItem: {},
};

export interface GetRiskKycPresetsListPayload {
  limit?: number;
  offset?: number;
};

export interface RiskKycPresetsListResponse {
  items: RiskKycPresetItem[];
  totalItems: number;
  totalPages: number;
}

export interface GetRiskKycPresetsListResponse {
  presets: RiskKycPresetsListResponse,
  rules: RiskKycPresetRule[]
};

const getRiskKycPresetsList = createAsyncThunk(
  "kyc-presets/list",
  async (data: GetRiskKycPresetsListPayload) => {
    const searchParams = new URLSearchParams();

    if (!!data.limit) searchParams.append('limit', data.limit.toString());
    if (!!data.offset) searchParams.append('offset', data.offset.toString());

    try {
      const presetsReponse = await instance.get(
        `${apiRoutes.riskKycPresetsList}${searchParams.toString() ? `?${searchParams}` : ''}`
      );

      const presets = presetsReponse.data as RiskKycPresetsListResponse;

      return {
        presets,
      };
      
    } catch (error) {
      throw error;
    }
  }
);

const getRiskKycPresetsItem = createAsyncThunk(
  "kyc-preset/get",
  async (id: number) => {
    try {
      // const itemResponse = await instance.get(
      //   `${apiRoutes.riskKycPresetsList}/${id}`
      // );

      // const rulesResponse = await instance.get(
      // `${apiRoutes.riskKycPresetsList}/${id}/rules`
      // );

      const result = await Promise.all([
        instance.get(
          `${apiRoutes.riskKycPresetsList}/${id}`
        ),
        instance.get(`${apiRoutes.riskKycPresetsList}/${id}/rules`)
      ]);

      const item = result[0].data as RiskKycPresetItem;
      const rules = result[1].data.items as Array<Omit<RiskKycPresetRule, 'rule'> & { rule: RiskKycRulesItemResponse }>;

      return { item, rules};
    } catch (error) {
      throw error;
    }
  }
);

const createRiskKycPresetsItem = createAsyncThunk(
  "kyc-preset/create",
  async (data: Pick<RiskKycPresetItem, 'title' | 'code' | 'description' | 'isActive'>) => {
    try {
      const result = await instance.post(
        `${apiRoutes.riskKycPresetsList}`,
        data
      );

      return result.data as RiskKycPresetItem;
    } catch (error: any) {
      throw error;
    }
  }
);

const updateRiskKycPresetsItem = createAsyncThunk(
  "kyc-preset/update",
  async (data: RiskKycPresetItem) => {
    try {
      const { id, createdAt, updatedAt, ...dataToUpdate } = data;
      
      const result = await instance.put(
        `${apiRoutes.riskKycPresetsList}/${id}`,
        dataToUpdate
      );

      return result.data as RiskKycPresetItem;
    } catch (error) {
      throw error;
    }
  }
);

const deleteRiskKycPresetsItem = createAsyncThunk(
  "kyc-preset/delete",
  async (id: number) => {
    try {
      const result = await instance.delete(`${apiRoutes.riskKycPresetsList}/${id}`);

      return true;
    } catch (error) {
      throw error;
    }
  }
);

const addRiskKycPresetsRules = createAsyncThunk(
  "kyc-preset/add-rules",
  async (data: {
    presetId: RiskKycPresetItem["id"],
    rules: {
      ruleDefinitionId: RiskKycRulesItem["id"],
      isActive: RiskKycRulesItem["isActive"],
      position: number
    }[]
  }) => {
    try {
      const result = await Promise.all(data.rules.map(rule => (
        instance.post(
          `${apiRoutes.riskKycPresetsList}/${data.presetId}/rules`,
          rule
        )
      )));

      return true;
      
    } catch (error) {
      throw error;
    }
  }
);

const deleteRiskKycPresetsRules = createAsyncThunk(
  "kyc-preset/delete-rules",
  async (id: RiskKycRulesItem["id"][]) => {
    try {
      const result = await Promise.all(id.map(ruleId => (
        instance.delete(
          `${apiRoutes.riskKycPresetRulesList}/${ruleId}`
        )
      )));

      return true;
      
    } catch (error) {
      throw error;
    }
  }
);

export const riskKycPresetSlice = createSlice({
  name: "riskKycPresets",
  initialState,
  reducers: {
    handlePaginate: (
      state: RiskKeycPresetState,
      action: PayloadAction<number>,
    ) => {
      state.activePage = action.payload;

      state.isListFetched = false;
    },

    handleClearList: (state: RiskKeycPresetState) => {
      state.list = [];

      state.activePage = 1;

      state.totalItems = 0;
      state.totalPages = 0;

      state.isListFetched = false;
    },
    handleSelect: (state, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        )
      } else {
        state.selectedItems.push(action.payload);
      }
    },
    handleSelectAll: (state) => {
      state.selectedItems = state.selectedItems.length !== state.list.length
        ? state.list.map((item) => item.id)
        : [];
    },
    handleClearDraftItem: (state: RiskKeycPresetState) => {
      state.draftItem = {};
      state.isItemFetched = false;
    },
    handleUpdateDraftItem: (
      state: RiskKeycPresetState,
      action: PayloadAction<Partial<RiskKycPresetItem>>
    ) => {
      state.draftItem = {
        ...state.draftItem,
        ...action.payload
      }
    }
  },

  extraReducers: (builder) => {
    builder.addCase(getRiskKycPresetsList.rejected, (state, action) => {
      state.isListFetched = true;
    });

    builder.addCase(getRiskKycPresetsList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.totalItems = action.payload?.presets.totalItems ?? 1;
      state.totalPages = action.payload?.presets.totalPages ?? 1;

      if (action.payload?.presets.items) {
        state.list = action.payload.presets.items.map(v => ({
          id: v.id,
          title: v.title ?? '',
          code: v.code ?? '',
          description: v.description ?? '',
          rules: '\-',
          isActive: v.isActive,
          createdAt: v.createdAt,
          updatedAt: v.updatedAt
        }));
      } else {
        state.list = [];
      }
    });

    builder.addCase(getRiskKycPresetsItem.pending, (state) => {
      state.isItemFetching = true;
    });

    builder.addCase(getRiskKycPresetsItem.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.isItemFetched = true;
      state.draftItem = action.payload.item;
      state.draftItem.assignedRules = action.payload.rules.map(r => {
        const { rule, ...rest } = r;
        const { configJson } = r.rule;
        const { amount, currency } = JSON.parse(configJson);
        return {
          ...rest,
          rule: {
            ...r.rule,
            amount,
            currency
          }
        }
      });
    });
    
    builder.addCase(getRiskKycPresetsItem.rejected, (state) => {
      state.isItemFetching = false;
    });

    builder.addCase(updateRiskKycPresetsItem.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });

    builder.addCase(updateRiskKycPresetsItem.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.list = state.list.map((item) =>
        item.id === action.payload.id
          ? mergeExistingFields(item, action.payload)
          : item,
      );
      state.processingItemId = undefined;
    });
    
    builder.addCase(updateRiskKycPresetsItem.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });

    builder.addCase(createRiskKycPresetsItem.pending, (state) => {
      state.isItemFetching = true;
    });

    builder.addCase(createRiskKycPresetsItem.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.isListFetched = false;
    });
    
    builder.addCase(createRiskKycPresetsItem.rejected, (state) => {
      state.isItemFetching = false;
    });

    builder.addCase(deleteRiskKycPresetsItem.pending, (state, action) => {
      state.processingItemId = action.meta.arg;
    });

    builder.addCase(deleteRiskKycPresetsItem.fulfilled, (state) => {
      state.processingItemId = undefined;
    });

    builder.addCase(deleteRiskKycPresetsItem.rejected, (state) => {
      state.processingItemId = undefined;
    });
    
    builder.addCase(addRiskKycPresetsRules.pending, (state) => {
      state.isItemFetching = true;
    });

    builder.addCase(addRiskKycPresetsRules.fulfilled, (state) => {
      state.isItemFetching = false;
      state.isItemFetched = false;
    });

    builder.addCase(addRiskKycPresetsRules.rejected, (state) => {
      state.isItemFetching = false;
    });

    builder.addCase(deleteRiskKycPresetsRules.pending, (state) => {
      state.isItemFetching = true;
    });

    builder.addCase(deleteRiskKycPresetsRules.fulfilled, (state) => {
      state.isItemFetching = false;
      state.isItemFetched = false;
    });

    builder.addCase(deleteRiskKycPresetsRules.rejected, (state) => {
      state.isItemFetching = false;
    });
  }
});

const {
  handlePaginate,
  handleClearList,
  handleSelect,
  handleSelectAll,
  handleClearDraftItem,
  handleUpdateDraftItem,
} = riskKycPresetSlice.actions;

export {
  handlePaginate,
  handleClearList,
  getRiskKycPresetsList,
  getRiskKycPresetsItem,
  handleSelect,
  handleSelectAll,
  handleClearDraftItem,
  handleUpdateDraftItem,
  updateRiskKycPresetsItem,
  createRiskKycPresetsItem,
  deleteRiskKycPresetsItem,
  addRiskKycPresetsRules,
  deleteRiskKycPresetsRules
};
